import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import changelog from '../data/changelog.json';
import { Logo, Icon } from './v3';

const STORAGE_KEY = 'ferrum_changelog_last_seen';

// v3.6.2 (F-09): `icon` is now a v3 inline-SVG glyph name (see v3/Icon.js),
// not an emoji — the design system forbids emoji in nav chrome.
const NAV = [
  { to: '/quotes',         label: 'Saved Quotes',         icon: 'quotes' },
  { to: '/market-rates',   label: 'Market Intelligence',  icon: 'market' },
  // v3.5.99.7.13: standalone ETF calculator. Per-quote calculator is
  // accessed via the ETF button on individual quote pages.
  { to: '/etf-calculator', label: 'ETF Calc',             icon: 'calculator' },
  // v3.5.99.7.18: ETF Letters list view — all generated ETFs, filterable
  { to: '/etf-letters',    label: 'ETF Letters',          icon: 'document' },
];
const ADMIN_NAV = [
  { to: '/admin',    label: 'Admin',        icon: 'settings' },
  { to: '/activity', label: 'Activity Log', icon: 'activity' },
];

export default function Layout() {
  const { profile, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [logoUrl,     setLogoUrl]     = useState(null);

  // Load logo from settings
  useEffect(() => {
    supabase.from('pricing_settings').select('value').eq('key', 'company_logo_url').single()
      .then(({ data }) => { if (data?.value) setLogoUrl(data.value); });
  }, []);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const lastSeen = localStorage.getItem(STORAGE_KEY);
    const count = lastSeen
      ? changelog.filter(r => r.version > lastSeen).length
      : changelog.length;
    setUnreadCount(count);
  }, []);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  // v3.6.2: larger 13px nav type, roomier padding, softer active state.
  const navStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '7px 11px', borderRadius: 7, fontSize: 13, fontWeight: 500,
    textDecoration: 'none', transition: 'background 0.14s ease-out, color 0.14s ease-out',
    background: isActive ? 'rgba(134,168,255,0.16)' : 'transparent',
    color: isActive ? '#ffffff' : '#94a3b8',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ background: '#0f1e3c', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 10, height: 52, flexShrink: 0 }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 8 }}>
          {logoUrl ? (
            <img src={logoUrl} alt="Company Logo"
              style={{ height: 28, maxWidth: 120, objectFit: 'contain', display: 'block' }} />
          ) : (
            // v3.6.0 (F-08): the FerrumIQ Pulse mark + wordmark is the product
            // identity. Renders when no company_logo_url is set in
            // pricing_settings. Clear that setting (proposed SQL in the v3.6.0
            // deploy notes) for reps to see this instead of the uploaded logo.
            <Logo size={26} onDark />
          )}
        </div>

        {/* New Quote dropdown */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setNewMenuOpen(o => !o)} style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px',
            background: '#2563eb', border: 'none', borderRadius: 5, color: 'white',
            fontSize: 12, fontWeight: 600, cursor: 'pointer'
          }}>
            + New Quote ▾
          </button>
          {newMenuOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 0', minWidth: 200, zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <button onClick={() => { navigate('/'); setNewMenuOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', fontSize: 12, color: '#374151', cursor: 'pointer', display: 'block' }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="monitor" /> Managed IT</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>Workstation, user, and cyber security</div>
              </button>
              <button onClick={() => { navigate('/voice'); setNewMenuOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', fontSize: 12, color: '#374151', cursor: 'pointer', display: 'block' }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="phone" /> Hosted Voice</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>VoIP seats, SIP trunking, hybrid hosting</div>
              </button>
              <div style={{ height: 1, background: '#f3f4f6', margin: '2px 0' }}/>
              <button onClick={() => { navigate('/bundle'); setNewMenuOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', fontSize: 12, color: '#374151', cursor: 'pointer', display: 'block' }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="package" /> Bundle Quote</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>Managed IT + Voice with bundle discount</div>
              </button>
              <button onClick={() => { navigate('/multisite'); setNewMenuOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', fontSize: 12, color: '#374151', cursor: 'pointer', display: 'block' }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="building" /> Multi-Site IT</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>Multiple locations — blended market rates + volume discount</div>
              </button>
              <div style={{ height: 1, background: '#f3f4f6', margin: '2px 0' }}/>
              <button onClick={() => { navigate('/flexIT/new'); setNewMenuOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', fontSize: 12, color: '#374151', cursor: 'pointer', display: 'block' }}>
                <div style={{ fontWeight: 600, color: '#f97316', display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="zap" /> FlexIT On-Demand</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>Time & materials — break/fix support</div>
              </button>
            </div>
          )}
        </div>

        {/* Nav links */}
        {[...NAV, ...(isAdmin ? ADMIN_NAV : [])].map(({ to, label, icon }) => (
          <NavLink key={to} to={to} end style={({ isActive }) => navStyle(isActive)}>
            <Icon name={icon} /> {label}
          </NavLink>
        ))}

        {/* What's New */}
        <NavLink to="/changelog" style={({ isActive }) => navStyle(isActive)} onClick={() => setUnreadCount(0)}>
          <Icon name="whatsNew" /> What's New
          {unreadCount > 0 && (
            <span style={{ fontSize: 9, fontWeight: 700, background: '#2563eb', color: 'white', padding: '1px 5px', borderRadius: 8, marginLeft: 2 }}>
              {unreadCount}
            </span>
          )}
        </NavLink>

        <div style={{ flex: 1 }} />

        {/* User menu */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setMenuOpen(o => !o)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
            background: 'transparent', border: '1px solid #334155', borderRadius: 4, color: '#94a3b8', fontSize: 11
          }}>
            <span style={{ width: 28, height: 28, background: '#2563eb', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 12, fontWeight: 700 }}>
              {profile?.full_name?.[0]?.toUpperCase() || profile?.email?.[0]?.toUpperCase() || '?'}
            </span>
            {profile?.full_name || profile?.email?.split('@')[0]}
            {isAdmin && <span style={{ fontSize: 9, background: '#1e40af', color: '#bfdbfe', padding: '1px 4px', borderRadius: 3 }}>admin</span>}
          </button>
          {menuOpen && (
            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 0', minWidth: 160, zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <div style={{ padding: '6px 12px', fontSize: 11, color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>{profile?.email}</div>
              <button onClick={handleSignOut} style={{ width: '100%', textAlign: 'left', padding: '6px 12px', background: 'none', border: 'none', fontSize: 12, color: '#374151', cursor: 'pointer' }}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Page content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </div>
    </div>
  );
}
